/**
 * Product Form Web Component
 * Handles add to cart functionality with AJAX
 */

if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      this.submitButton = this.querySelector('[type="submit"]');
      this.errorWrapper = this.querySelector('.product-form__error-message-wrapper');
      this.errorMessage = this.querySelector('.product-form__error-message');
    }

    connectedCallback() {
      if (!this.form) return;
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
    }

    async onSubmitHandler(event) {
      event.preventDefault();

      if (this.submitButton.disabled) return;

      this.handleErrorMessage();
      this.setLoading(true);

      const formData = new FormData(this.form);
      const config = this.getFetchConfig('javascript');

      // Build cart add body
      const body = {
        items: [{
          id: parseInt(formData.get('id')),
          quantity: parseInt(formData.get('quantity')) || 1
        }],
        sections: this.getSectionsToRender().map((section) => section.id),
        sections_url: window.location.pathname
      };

      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          ...config,
          body: JSON.stringify(body)
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || data.description || 'Error adding to cart');
        }

        // Success - update cart UI
        this.onAddSuccess(data);

      } catch (error) {
        this.handleErrorMessage(error.message);
      } finally {
        this.setLoading(false);
      }
    }

    onAddSuccess(data) {
      // Dispatch custom event for cart drawer/notification
      document.dispatchEvent(new CustomEvent('cart:item-added', {
        detail: {
          items: data.items,
          sections: data.sections
        },
        bubbles: true
      }));

      // Try to update cart count in header
      this.updateCartCount(data);

      // Show success feedback
      this.showSuccessFeedback();

      // If KensoCart is available, trigger refresh
      if (window.KensoCart && typeof window.KensoCart.get === 'function') {
        window.KensoCart.get();
      }
    }

    updateCartCount(data) {
      // Try multiple common cart count selectors
      const cartCountSelectors = [
        '[data-cart-count]',
        '.cart-count',
        '.cart-count-bubble',
        '#cart-icon-bubble span',
        '.header__cart-count'
      ];

      let totalItems = 0;

      // Calculate total items from cart data
      if (data.items) {
        totalItems = data.items.reduce((sum, item) => sum + item.quantity, 0);
      }

      // Fetch current cart to get accurate count
      fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)
        .then(res => res.json())
        .then(cart => {
          cartCountSelectors.forEach(selector => {
            const elements = document.querySelectorAll(selector);
            elements.forEach(el => {
              el.textContent = cart.item_count;
              if (cart.item_count > 0) {
                el.classList.remove('hidden');
              }
            });
          });
        })
        .catch(() => {
          // Fallback: just update with what we have
          cartCountSelectors.forEach(selector => {
            const elements = document.querySelectorAll(selector);
            elements.forEach(el => {
              const current = parseInt(el.textContent) || 0;
              el.textContent = current + 1;
            });
          });
        });
    }

    showSuccessFeedback() {
      const button = this.submitButton;
      const originalText = button.querySelector('.product-form__submit-text')?.textContent;

      // Temporarily show success state
      if (button.querySelector('.product-form__submit-text')) {
        button.querySelector('.product-form__submit-text').textContent = 'Added!';
        button.classList.add('product-form__submit--success');

        setTimeout(() => {
          button.querySelector('.product-form__submit-text').textContent = originalText;
          button.classList.remove('product-form__submit--success');
        }, 2000);
      }
    }

    setLoading(isLoading) {
      if (isLoading) {
        this.submitButton.setAttribute('disabled', 'disabled');
        this.submitButton.classList.add('loading');
      } else {
        this.submitButton.removeAttribute('disabled');
        this.submitButton.classList.remove('loading');
      }

      // Toggle spinner visibility
      const spinner = this.submitButton.querySelector('.product-form__submit-spinner');
      if (spinner) {
        spinner.classList.toggle('hidden', !isLoading);
      }
    }

    handleErrorMessage(message = '') {
      if (!this.errorWrapper || !this.errorMessage) return;

      if (message) {
        this.errorMessage.textContent = message;
        this.errorWrapper.removeAttribute('hidden');
      } else {
        this.errorMessage.textContent = '';
        this.errorWrapper.setAttribute('hidden', '');
      }
    }

    getFetchConfig(type = 'json') {
      return {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': `application/${type}`
        }
      };
    }

    getSectionsToRender() {
      // Return sections that should be updated after cart add
      return [
        {
          id: 'cart-icon-bubble',
          selector: '#cart-icon-bubble'
        },
        {
          id: 'cart-drawer',
          selector: '#cart-drawer'
        }
      ];
    }
  });
}

// Add success styling
const successStyles = document.createElement('style');
successStyles.textContent = `
  .product-form__submit--success {
    background: var(--color-success) !important;
    border-color: var(--color-success) !important;
    color: #fff !important;
  }
`;
document.head.appendChild(successStyles);
